import { useState, useEffect } from "react";
import { Card, CardContent } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Checkbox } from "../../components/ui/checkbox";
import { Label } from "../../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../../components/ui/dialog";
import { AdminRecordModal, AdminModalFooter } from "../../components/admin/AdminRecordModal";
import { toast } from "sonner";
import { Plus, CreditCard, Trash, Buildings } from "phosphor-react";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

export default function AdminSubscriptions() {
  const [subscriptions, setSubscriptions] = useState([]);
  const [tenants, setTenants] = useState([]);
  const [features, setFeatures] = useState([]);
  const [bundles, setBundles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [formData, setFormData] = useState({ tenant_id: "", feature_ids: [], bundle_id: "", billing_cycle: "monthly" });

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    try {
      const [subsRes, tenantsRes, featuresRes, bundlesRes] = await Promise.all([
        fetch(`${API}/admin/subscriptions`, { credentials: "include" }),
        fetch(`${API}/admin/tenants`, { credentials: "include" }),
        fetch(`${API}/admin/features`, { credentials: "include" }),
        fetch(`${API}/admin/bundles`, { credentials: "include" })
      ]);
      if (subsRes.ok) setSubscriptions((await subsRes.json()).subscriptions);
      if (tenantsRes.ok) setTenants((await tenantsRes.json()).tenants);
      if (featuresRes.ok) setFeatures((await featuresRes.json()).features);
      if (bundlesRes.ok) setBundles((await bundlesRes.json()).bundles);
    } catch (error) {
      console.error("Error:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const response = await fetch(`${API}/admin/subscriptions`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        credentials: "include", body: JSON.stringify(formData)
      });
      if (response.ok) {
        toast.success("Subscription created");
        setIsDialogOpen(false);
        resetForm();
        fetchData();
      } else {
        toast.error("Failed to create subscription");
      }
    } catch (error) {
      toast.error("Failed to create");
    }
  };

  const cancelSubscription = async (subscriptionId) => {
    if (!window.confirm("Cancel this subscription?")) return;
    try {
      await fetch(`${API}/admin/subscriptions/${subscriptionId}`, { method: "DELETE", credentials: "include" });
      toast.success("Subscription cancelled");
      fetchData();
    } catch (error) {
      toast.error("Failed to cancel");
    }
  };

  const resetForm = () => {
    setFormData({ tenant_id: "", feature_ids: [], bundle_id: "", billing_cycle: "monthly" });
  };

  const toggleFeature = (featureId) => {
    setFormData(prev => ({
      ...prev,
      feature_ids: prev.feature_ids.includes(featureId) ? prev.feature_ids.filter(id => id !== featureId) : [...prev.feature_ids, featureId]
    }));
  };

  // Filter out tenants that already have subscriptions
  const subscribedTenantIds = subscriptions.map(s => s.tenant_id);
  const availableTenants = tenants.filter(t => !subscribedTenantIds.includes(t.tenant_id));

  return (
    <div className="space-y-6" data-testid="admin-subscriptions">
      <div className="flex items-center justify-between">
        <h1 className="font-heading text-2xl font-bold text-white uppercase">Subscriptions</h1>
        <Button onClick={() => setIsDialogOpen(true)} className="bg-[#FF6600] hover:bg-[#CC5200] text-white"><Plus size={18} className="mr-2" />Add Subscription</Button>
        <AdminRecordModal
          open={isDialogOpen}
          onOpenChange={(open) => { setIsDialogOpen(open); if (!open) resetForm(); }}
          title="Create Subscription"
          size="md"
          footer={
            <AdminModalFooter onCancel={() => { setIsDialogOpen(false); resetForm(); }}>
              <Button onClick={handleSubmit} disabled={!formData.tenant_id} className="bg-[#FF6600] hover:bg-[#CC5200] text-white">Create</Button>
            </AdminModalFooter>
          }
        >
          <div className="space-y-1.5">
            <Label className="text-sm font-medium text-zinc-300">Tenant</Label>
            <Select value={formData.tenant_id} onValueChange={(v) => setFormData({ ...formData, tenant_id: v })}>
              <SelectTrigger className="bg-zinc-800 border-zinc-700"><SelectValue placeholder="Select tenant" /></SelectTrigger>
              <SelectContent>
                {availableTenants.map((tenant) => (
                  <SelectItem key={tenant.tenant_id} value={tenant.tenant_id}>{tenant.company_name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label className="text-sm font-medium text-zinc-300">Bundle (optional)</Label>
            <Select value={formData.bundle_id} onValueChange={(v) => setFormData({ ...formData, bundle_id: v, feature_ids: [] })}>
              <SelectTrigger className="bg-zinc-800 border-zinc-700"><SelectValue placeholder="Select bundle" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="">No bundle (individual features)</SelectItem>
                {bundles.map((bundle) => (
                  <SelectItem key={bundle.bundle_id} value={bundle.bundle_id}>{bundle.name} - ${bundle.price_monthly}/mo</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          {!formData.bundle_id && (
            <div className="space-y-1.5">
              <Label className="text-sm font-medium text-zinc-300">Individual Features</Label>
              <div className="mt-2 space-y-2 max-h-48 overflow-y-auto border border-zinc-700/50 rounded-lg p-2 bg-zinc-800/30">
                {features.map((feature) => (
                  <label key={feature.feature_id} className="flex items-center gap-3 p-2 rounded-lg cursor-pointer hover:bg-zinc-800">
                    <Checkbox checked={formData.feature_ids.includes(feature.feature_id)} onCheckedChange={() => toggleFeature(feature.feature_id)} />
                    <span className="flex-1 text-white">{feature.name}</span>
                    <span className="text-xs text-zinc-500">${feature.price_monthly}/mo</span>
                  </label>
                ))}
              </div>
            </div>
          )}
          <div className="space-y-1.5">
            <Label className="text-sm font-medium text-zinc-300">Billing Cycle</Label>
            <Select value={formData.billing_cycle} onValueChange={(v) => setFormData({ ...formData, billing_cycle: v })}>
              <SelectTrigger className="bg-zinc-800 border-zinc-700"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="monthly">Monthly</SelectItem>
                <SelectItem value="yearly">Yearly</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </AdminRecordModal>
      </div>

      {loading ? (
        <div className="space-y-4">{[...Array(5)].map((_, i) => <div key={i} className="h-20 bg-zinc-800 rounded-sm animate-pulse" />)}</div>
      ) : subscriptions.length > 0 ? (
        <div className="space-y-3">
          {subscriptions.map((sub) => (
            <Card key={sub.subscription_id} className="bg-zinc-900 border-zinc-800">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 bg-zinc-800 flex items-center justify-center rounded-sm">
                      <Buildings size={24} className="text-zinc-500" />
                    </div>
                    <div>
                      <p className="font-medium text-white">{sub.tenant?.company_name}</p>
                      <p className="text-sm text-zinc-500">{sub.bundle_id ? "Bundle" : `${sub.feature_ids?.length || 0} features`} • {sub.billing_cycle}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    <div className="text-right">
                      <p className="text-xl font-bold text-emerald-400">${sub.monthly_amount?.toFixed(2)}<span className="text-sm text-zinc-500">/mo</span></p>
                      <Badge className={sub.status === "active" ? "bg-emerald-500" : "bg-zinc-600"}>{sub.status}</Badge>
                    </div>
                    <Button variant="ghost" size="icon" onClick={() => cancelSubscription(sub.subscription_id)} className="text-red-400 hover:text-red-300">
                      <Trash size={18} />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      ) : (
        <Card className="bg-zinc-900 border-zinc-800 border-dashed">
          <CardContent className="p-12 text-center">
            <CreditCard size={48} className="mx-auto mb-4 text-zinc-600" />
            <p className="text-zinc-400">No subscriptions yet</p>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
